import { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetBookingUninvoicedReservationPaymentsReportQuery } from '../Service/reportsApi';
import { extractApiErrorMessage } from '../../../Utils/apiErrorUtils';
import { BookingsRoutes, ReportsRoutes } from '../../../Router/path';
import type { GetBookingUninvoicedReservationPaymentsReportParams } from '../Types/reports.types';
import type {
  BookingReturnToReportNavigationState,
  UninvoicedReservationPaymentsReportFiltersSnapshot,
  UninvoicedReservationPaymentsReportRestoreNavigationState,
} from '../../Shared/Types/reportsNavigationTypes';

const DEFAULT_PAGE_SIZE = 10;

export interface UninvoicedReservationPaymentsReportSearchFilters {
  companyId?: number;
  bookingDateToFrom?: string;
  bookingDateToTo?: string;
}

const defaultSearchFilters: UninvoicedReservationPaymentsReportSearchFilters = {
  companyId: undefined,
  bookingDateToFrom: undefined,
  bookingDateToTo: undefined,
};

const toComparableDate = (value?: string): number | null => {
  if (!value) {
    return null;
  }

  const timestamp = Date.parse(value.slice(0, 10));
  return Number.isNaN(timestamp) ? null : timestamp;
};

const hasInvalidDateRange = (values: UninvoicedReservationPaymentsReportSearchFilters): boolean => {
  const fromTimestamp = toComparableDate(values.bookingDateToFrom);
  const toTimestamp = toComparableDate(values.bookingDateToTo);

  if (fromTimestamp === null || toTimestamp === null) {
    return false;
  }

  return fromTimestamp > toTimestamp;
};

// Reads only the discriminated field owned by this report (`restoredUninvoicedReportFilters`) and
// verifies its self-tag, so a restore state produced for the old unsettled-bookings-accounting
// report (which never sets this field) is ignored rather than partially consumed.
const readRestoredFilters = (
  state: unknown
): UninvoicedReservationPaymentsReportFiltersSnapshot | undefined => {
  const restored = (state as UninvoicedReservationPaymentsReportRestoreNavigationState | null)
    ?.restoredUninvoicedReportFilters;

  return restored?.reportType === 'uninvoicedReservationPaymentsReport' ? restored : undefined;
};

export const useUninvoicedReservationPaymentsReportPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const restoredReportFilters = readRestoredFilters(location.state);

  const [draftFilters, setDraftFilters] =
    useState<UninvoicedReservationPaymentsReportSearchFilters>(() =>
      restoredReportFilters
        ? {
            companyId: restoredReportFilters.companyId,
            bookingDateToFrom: restoredReportFilters.bookingDateToFrom,
            bookingDateToTo: restoredReportFilters.bookingDateToTo,
          }
        : defaultSearchFilters
    );
  const [filters, setFilters] = useState<Partial<GetBookingUninvoicedReservationPaymentsReportParams>>(
    () =>
      restoredReportFilters
        ? {
            companyId: restoredReportFilters.companyId,
            bookingDateToFrom: restoredReportFilters.bookingDateToFrom,
            bookingDateToTo: restoredReportFilters.bookingDateToTo,
            pageNumber: restoredReportFilters.pageNumber,
            pageSize: restoredReportFilters.pageSize,
          }
        : { ...defaultSearchFilters, pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE }
  );
  const [expandedBookingIds, setExpandedBookingIds] = useState<number[]>(
    () => restoredReportFilters?.expandedBookingIds ?? []
  );
  const [validationError, setValidationError] = useState<string>('');

  const hasCompanyId = typeof filters.companyId === 'number' && filters.companyId > 0;

  const { data, isLoading, isFetching, isError, error } =
    useGetBookingUninvoicedReservationPaymentsReportQuery(
      hasCompanyId
        ? (filters as GetBookingUninvoicedReservationPaymentsReportParams)
        : { companyId: 0, pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE },
      { skip: !hasCompanyId }
    );

  const queryErrorMessage = isError ? extractApiErrorMessage(error, 'Nie udało się pobrać raportu.') : null;

  const handleDraftFilterChange = useCallback(
    (next: Partial<UninvoicedReservationPaymentsReportSearchFilters>) => {
      setDraftFilters((prev) => ({ ...prev, ...next }));
    },
    []
  );

  const handleSearchSubmit = useCallback(() => {
    if (hasInvalidDateRange(draftFilters)) {
      setValidationError(
        "Nieprawidłowy zakres dat: 'Zakończenie od' nie może być późniejsza niż 'Zakończenie do'."
      );
      return;
    }

    setValidationError('');
    setFilters((prev) => ({ ...prev, ...draftFilters, pageNumber: 1 }));
    setExpandedBookingIds([]);
  }, [draftFilters]);

  const handleResetFilters = useCallback(() => {
    setValidationError('');
    setDraftFilters(defaultSearchFilters);
    setFilters((prev) => ({ ...prev, ...defaultSearchFilters, pageNumber: 1 }));
    setExpandedBookingIds([]);
  }, []);

  const handlePageChange = useCallback((pageNumber: number) => {
    setFilters((prev) => ({ ...prev, pageNumber }));
  }, []);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setFilters((prev) => ({ ...prev, pageSize, pageNumber: 1 }));
  }, []);

  const toggleExpandedBooking = useCallback((bookingId: number) => {
    setExpandedBookingIds((prev) =>
      prev.includes(bookingId) ? prev.filter((id) => id !== bookingId) : [...prev, bookingId]
    );
  }, []);

  const goToBookingEdit = useCallback(
    (bookingId: number) => {
      const updatePath = BookingsRoutes.update.replace(':bookingId', bookingId.toString());
      const returnTo = encodeURIComponent(ReportsRoutes.uninvoicedReservationPayments);
      const uninvoicedReportReturnContext: UninvoicedReservationPaymentsReportFiltersSnapshot = {
        reportType: 'uninvoicedReservationPaymentsReport',
        companyId: filters.companyId,
        bookingDateToFrom: filters.bookingDateToFrom,
        bookingDateToTo: filters.bookingDateToTo,
        pageNumber: filters.pageNumber ?? 1,
        pageSize: filters.pageSize ?? DEFAULT_PAGE_SIZE,
        expandedBookingIds,
      };

      navigate(`${updatePath}?origin=uninvoiced-reservation-payments-report&returnTo=${returnTo}`, {
        state: { uninvoicedReportReturnContext } satisfies BookingReturnToReportNavigationState,
      });
    },
    [navigate, filters, expandedBookingIds]
  );

  return {
    data,
    isLoading,
    isFetching,
    queryErrorMessage,
    validationError,
    draftFilters,
    filters,
    hasCompanyId,
    expandedBookingIds,
    handleDraftFilterChange,
    handleSearchSubmit,
    handleResetFilters,
    handlePageChange,
    handlePageSizeChange,
    toggleExpandedBooking,
    goToBookingEdit,
  };
};
